import {
  IconArrowRight,
  IconBus,
  IconCalendar,
  IconClock,
  IconHeart,
  IconHeartFilled,
  IconMapPin,
  IconPhotoFilled,
} from "@tabler/icons-react";
import { useState } from "react";
import { Link } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useLocaleContext } from "src/context/locale-context";
import { favoritesActions, selectIsFavorited } from "src/store/favorites";
import { FallbackImage } from "src/components/features/tours/FallbackImage";
import { getCountryCoverUrl } from "src/components/features/tours/getCountryImageUrl";
import { getDaysText, getTourText, splitTourName } from "src/components/features/tours/tour-text-utils";
import { FLIGHT_DATE_FORMAT, getDisplayDateValue } from "src/utils/date-utils";
import classes from "./BusTourCards.module.css";

const TONE_CLASS = {
  rose: "badgeRose",
  sky: "badgeSky",
  mint: "badgeMint",
  amber: "badgeAmber",
  violet: "badgeViolet",
};

function TourImage({ tour, className, iconSize }) {
  const imageUrl = getCountryCoverUrl(tour.country, tour.code);
  const [imgErr, setImgErr] = useState(false);
  return imageUrl && !imgErr ? (
    <FallbackImage
      className={className}
      src={imageUrl}
      alt={tour.country}
      onFinalError={() => setImgErr(true)}
    />
  ) : (
    <div className={classes.imageFallback}>
      <IconPhotoFilled size={iconSize} color="var(--ui-4)" />
    </div>
  );
}

function BusBadge() {
  const { getStringRes } = useLocaleContext();
  return (
    <span className={classes.busBadge}>
      <IconBus size={15} stroke={1.75} />
      {getStringRes("tours.card.busTrip")}
    </span>
  );
}

function CategoryBadge({ badge }) {
  if (!badge) return null;
  return <span className={`${classes.categoryBadge} ${classes[TONE_CLASS[badge.tone]]}`}>{badge.label}</span>;
}

// Same favoriting as TourCard on /bus-tours (favoriteId `tour-${code}`, type "tour") — these
// homepage carousel cards just never had the heart button wired in before.
function FavoriteButton({ tour }) {
  const { getStringRes } = useLocaleContext();
  const dispatch = useDispatch();
  const favoriteId = `tour-${tour.code}`;
  const isFavorite = useSelector((state) => selectIsFavorited(state, favoriteId));

  const onToggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(favoritesActions.toggleFavorite({ id: favoriteId, type: "tour", data: tour }));
    if (!isFavorite) {
      toast.success(getStringRes("favorites.toast.saved"));
    }
  };

  return (
    <button
      type="button"
      className={classes.favoriteButton}
      aria-label={getStringRes("cheap.hotels.favorite")}
      onClick={onToggleFavorite}>
      {isFavorite ? (
        <IconHeartFilled size={16} color="#ff2965" />
      ) : (
        <IconHeart size={16} stroke={1.75} />
      )}
    </button>
  );
}

export function BusTourFeaturedCard({ tour, badge }) {
  const { getStringRes } = useLocaleContext();
  const { title, subtitle } = splitTourName(tour.name);

  return (
    <div className={classes.favoriteWrap}>
      <FavoriteButton tour={tour} />
      <Link to={`/bus-tours/${tour.code}`} className={classes.featuredCard}>
      <div className={classes.featuredImageWrap}>
        <TourImage tour={tour} className={classes.featuredImage} iconSize={40} />
        <BusBadge />
        <CategoryBadge badge={badge} />
      </div>
      <div className={classes.featuredBody}>
        <h3 className={classes.featuredTitle}>{title}</h3>
        {subtitle && <p className={classes.featuredSubtitle}>{subtitle}</p>}

        <div className={classes.featuredMetaRow}>
          <span className={classes.metaItem}>
            <IconMapPin size={16} stroke={1.5} />
            {tour.country}
          </span>
          {tour.departureDate && (
            <span className={classes.metaItem}>
              <IconCalendar size={16} stroke={1.5} />
              {getDisplayDateValue(tour.departureDate, FLIGHT_DATE_FORMAT)}
            </span>
          )}
          {Number.isFinite(tour.days) && (
            <span className={classes.metaItem}>
              <IconClock size={16} stroke={1.5} />
              {getDaysText(tour.days, getStringRes)}
            </span>
          )}
        </div>

        <hr className={classes.divider} />

        <div className={classes.featuredFooter}>
          {Number.isFinite(tour.price) ? (
            <div>
              <span className={classes.priceLabel}>
                {tour.dateCount > 1
                  ? getStringRes("cheap.flights.price.from")
                  : getTourText(getStringRes, "tours.card.only")}
              </span>
              <div className={classes.priceLine}>
                <span className={classes.priceAmount}>{tour.price.toFixed(2)} €</span>
              </div>
            </div>
          ) : (
            <span />
          )}
          <span className={classes.ctaButton}>
            {getStringRes("tours.info.reserve")}
            <IconArrowRight size={18} stroke={2} />
          </span>
        </div>
      </div>
      </Link>
    </div>
  );
}

export function BusTourGridCard({ tour, badge }) {
  const { getStringRes } = useLocaleContext();
  const { title } = splitTourName(tour.name);

  return (
    <div className={classes.favoriteWrap}>
      <FavoriteButton tour={tour} />
      <Link to={`/bus-tours/${tour.code}`} className={classes.gridCard}>
      <div className={classes.gridImageWrap}>
        <TourImage tour={tour} className={classes.gridImage} iconSize={28} />
        <BusBadge />
        <CategoryBadge badge={badge} />
      </div>
      <div className={classes.gridBody}>
        <h4 className={classes.gridTitle}>{title}</h4>
        <div className={classes.gridMetaRow}>
          <span className={classes.metaItem}>
            <IconMapPin size={14} stroke={1.5} />
            {tour.country}
          </span>
          {tour.departureDate && (
            <span className={classes.metaItem}>
              <IconCalendar size={14} stroke={1.5} />
              {getDisplayDateValue(tour.departureDate, FLIGHT_DATE_FORMAT)}
            </span>
          )}
          {Number.isFinite(tour.days) && (
            <span className={classes.metaItem}>
              <IconClock size={14} stroke={1.5} />
              {getDaysText(tour.days, getStringRes)}
            </span>
          )}
        </div>
        <div className={classes.gridFooter}>
          {Number.isFinite(tour.price) ? (
            <div>
              <span className={classes.priceLabelSm}>
                {tour.dateCount > 1
                  ? getStringRes("cheap.flights.price.from")
                  : getTourText(getStringRes, "tours.card.only")}
              </span>
              <span className={classes.priceAmountSm}>{tour.price.toFixed(2)} €</span>
            </div>
          ) : (
            <span />
          )}
          <span className={classes.ctaButtonSm}>
            <IconArrowRight size={16} stroke={2} />
          </span>
        </div>
      </div>
      </Link>
    </div>
  );
}
